import { formatByteSize } from "@/shared/text";
import { Resource } from "@/types/resources";

/**
 * Backend sentinel (``QUANTITY_UNLIMITED``) meaning a limit has no cap.
 */
export const UNLIMITED_QUANTITY = -1;

const ONE_MILLION = 1_000_000;

interface LimitDisplayConfig {
  label: string;
  format: (quantity: number) => string;
  /**
   * Set when admins should enter the limit in a larger unit than the stored
   * quantity (e.g. credits, not credit millionths). ``scale`` is how many
   * stored units make one input unit.
   */
  input?: { unit: string; scale: number };
}

const CREDIT_INPUT = { unit: "credits", scale: ONE_MILLION };

function formatCount(quantity: number): string {
  return quantity.toLocaleString();
}

// Strip trailing zeros so the label reads "10 credits", not "10.00 credits"
// (same convention as the AI credits card's aiCreditFormatter).
function formatCredits(quantity: number): string {
  return `${+(quantity / ONE_MILLION).toFixed(2)} credits`;
}

function formatBytes(quantity: number): string {
  return formatByteSize(quantity);
}

// Display config keyed by wire name. Keys use the Resource enum so they are
// typo-proof, but lookups go through plain strings: the rows rendered by the
// limits editor come straight from the server, which may know resource names
// this frontend doesn't yet. Unknown names fall back to the raw name and a
// plain number — never assume this map is exhaustive.
const LIMIT_DISPLAY: Partial<Record<string, LimitDisplayConfig>> = {
  [Resource.ActionCount]: { label: "Total Actions", format: formatCount },
  [Resource.ActionTimeout]: {
    label: "Action Timeout",
    format: (q) => `${formatCount(q)} min`,
  },
  [Resource.AiCreditMillionths]: {
    label: "Agent Units (monthly)",
    format: (q) => `${+(q / ONE_MILLION).toFixed(2)} RAU`,
    input: { unit: "RAU", scale: ONE_MILLION },
  },
  [Resource.ConcurrentInvocations]: {
    label: "Concurrent Invocations",
    format: formatCount,
  },
  [Resource.CustomFieldsPerEntityType]: {
    label: "Custom Fields per Entity Type",
    format: formatCount,
  },
  [Resource.DatasetCount]: { label: "Total Datasets", format: formatCount },
  [Resource.DatasetsCreated]: {
    label: "Datasets Created",
    format: formatCount,
  },
  [Resource.FreeInvocationComputeCreditMillionths]: {
    label: "Free Compute Credits",
    format: formatCredits,
    input: CREDIT_INPUT,
  },
  [Resource.FreeManagedDataDownloadBytes]: {
    label: "Free Data Download",
    format: formatBytes,
  },
  [Resource.FreeManagedDataStoredBytes]: {
    label: "Free Data Storage",
    format: formatBytes,
  },
  [Resource.GPUs]: { label: "GPUs per Invocation", format: formatCount },
  [Resource.InvocationComputeCreditMillionths]: {
    label: "Compute Credits (monthly)",
    format: formatCredits,
    input: CREDIT_INPUT,
  },
  [Resource.InvocationVCpuMillis]: {
    label: "Invocation vCPU Time",
    format: (q) => `${formatCount(q)} vCPU-ms`,
  },
  [Resource.InvocationsScheduled]: {
    label: "Invocations Scheduled",
    format: formatCount,
  },
  [Resource.ManagedDataDownloadBytes]: {
    label: "Data Downloaded",
    format: formatBytes,
  },
  [Resource.ManagedDataStoredBytes]: {
    label: "Total Storage",
    format: formatBytes,
  },
  [Resource.MemoryMiBs]: {
    label: "Memory per Invocation",
    format: (q) => `${formatCount(q)} MiB`,
  },
  [Resource.OrgMembers]: { label: "Total Org Members", format: formatCount },
  [Resource.OrgServiceUsers]: { label: "Service Users", format: formatCount },
  [Resource.OwnedOrgsPerUser]: {
    label: "Orgs Owned per User",
    format: formatCount,
  },
  [Resource.StorageGiBs]: {
    label: "Storage per Invocation",
    format: (q) => `${formatCount(q)} GiB`,
  },
  [Resource.TriggersActive]: { label: "Active Triggers", format: formatCount },
  [Resource.VCPUs]: { label: "vCPUs per Invocation", format: formatCount },
};

/**
 * Human-readable label for a limit resource, falling back to the raw wire
 * name for resources this frontend doesn't know yet.
 */
export function limitLabel(resourceName: string): string {
  return LIMIT_DISPLAY[resourceName]?.label ?? resourceName;
}

/**
 * Render a limit quantity in the resource's natural unit (bytes, credits,
 * minutes, plain count). ``-1`` renders as "Unlimited" for every resource;
 * unknown resources render as a plain localized number.
 */
export function formatLimitQuantity(
  resourceName: string,
  quantity: number,
): string {
  if (quantity === UNLIMITED_QUANTITY) {
    return "Unlimited";
  }

  const format = LIMIT_DISPLAY[resourceName]?.format ?? formatCount;
  return format(quantity);
}

/**
 * Unit the limits editor takes input in, or ``undefined`` when the input is
 * the stored quantity itself.
 */
export function limitInputUnit(resourceName: string): string | undefined {
  return LIMIT_DISPLAY[resourceName]?.input?.unit;
}

/** Stored quantity → editor text, in the resource's input unit. */
export function limitQuantityToInput(
  resourceName: string,
  quantity: number,
): string {
  const scale = LIMIT_DISPLAY[resourceName]?.input?.scale ?? 1;
  if (quantity === UNLIMITED_QUANTITY) {
    return String(UNLIMITED_QUANTITY);
  }
  return String(quantity / scale);
}

/**
 * Editor text → stored quantity, or ``null`` when the text isn't a
 * non-negative amount (or -1) that converts to a whole stored quantity.
 */
export function limitQuantityFromInput(
  resourceName: string,
  input: string,
): number | null {
  if (input.trim() === "") {
    return null;
  }
  const parsed = Number(input);
  if (parsed === UNLIMITED_QUANTITY) {
    return UNLIMITED_QUANTITY;
  }
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  const scale = LIMIT_DISPLAY[resourceName]?.input?.scale ?? 1;
  const quantity = Math.round(parsed * scale);
  // Rounding absorbs float noise (0.1 * 1e6); anything further off means the
  // input was more precise than the stored unit can hold.
  if (Math.abs(quantity - parsed * scale) > 1e-6) {
    return null;
  }
  return quantity;
}
